import React, { useState } from 'react';
import '../../styles/sections/PricingSection.css';
import { loadStripe } from '@stripe/stripe-js';
import { analytics } from '../../utils/analytics';

const PricingSection = ({ 
  quantity, 
  setQuantity,
  isExpressShipping,
  setIsExpressShipping,
  couponCode,
  setCouponCode,
  couponDiscount,
  setCouponDiscount,
  originalImage,
  processedImage,
  imagePosition,
  imageScale,
  rawImageFile
}) => {
  const basePrice = 19.99;  // Price per tile in AUD
  const expressShippingPrice = 5.00;  // Express shipping cost
  const standardShippingPrice = 0;  // Standard shipping is free
  const pickupDiscount = 10.00;  // Pickup discount amount

  // Add state for error handling
  const [checkoutError, setCheckoutError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const calculateTotal = () => {
    const subtotal = basePrice * quantity;
    const shipping = isExpressShipping ? expressShippingPrice : standardShippingPrice;
    return Math.max(0, subtotal + shipping - couponDiscount);
  };

  const handleApplyCoupon = () => {
    if (couponCode.toLowerCase() === 'pickup') {
      setCouponDiscount(pickupDiscount);
      setCouponCode('');
    } else {
      setCouponDiscount(0);
      setCouponCode('');
    }
  };

  const handleCheckout = async () => {
    if (!originalImage) {
      alert('Please upload an image first');
      return;
    }

    // Track checkout initiation
    analytics.beginCheckout(
      calculateTotal(),
      quantity,
      isExpressShipping,
      couponDiscount > 0
    );

    // Reset error state and set processing state
    setCheckoutError(null);
    setIsProcessing(true);

    try {
      // Create canvas for image processing
      const createCanvasImage = async (imageUrl, greyScale = false) => {
        const img = new Image();
        img.src = imageUrl;
        await new Promise(resolve => { img.onload = resolve; });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to 100mm x 100mm at 300dpi
        canvas.width = 1181;  // 100mm at 300dpi
        canvas.height = 1181; // 100mm at 300dpi
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Calculate initial scale to fit image within canvas
        let initialScale;
        if (img.width > img.height) {
          initialScale = canvas.width / img.width;
        } else {
          initialScale = canvas.height / img.height;
        }

        // For processed image: apply user scaling and position
        const finalScale = initialScale * imageScale;
        ctx.drawImage(
          img,
          imagePosition.x,
          imagePosition.y,
          img.width * finalScale,
          img.height * finalScale
        );

        // Apply grayscale if needed
        if (greyScale) {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          for (let i = 0; i < data.length; i += 4) {
            // Skip if it's white background (R, G, and B all 255)
            if (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) continue;
            
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i] = data[i + 1] = data[i + 2] = avg;
          }
          
          ctx.putImageData(imageData, 0, 0);
        }

        return canvas.toDataURL('image/jpeg');
      };

      // Create canvas images
      const originalDataUrl = await createCanvasImage(originalImage, false);
      const processedDataUrl = await createCanvasImage(originalImage, true);

      // Convert images to blobs
      const originalBlob = await fetch(originalDataUrl).then(r => r.blob());
      const processedBlob = await fetch(processedDataUrl).then(r => r.blob());
      const rawBlob = await fetch(originalImage).then(r => r.blob());

      const formData = new FormData();
      formData.append('originalImage', originalBlob, 'original.jpg');
      formData.append('processedImage', processedBlob, 'processed.jpg');
      formData.append('rawImage', rawBlob, 'raw.jpg');
      formData.append('quantity', quantity.toString());
      formData.append('isExpressShipping', isExpressShipping.toString());
      formData.append('couponDiscount', couponDiscount.toString());

      // Check if API URL is defined
      if (!process.env.REACT_APP_API_URL) {
        throw new Error('API URL is not configured. Please check your environment variables.');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout-session`, {
        method: 'POST',
        body: formData,
      }).catch(error => {
        // Network error handling
        throw new Error(`Network error: ${error.message || 'Unable to connect to the server. Please check your internet connection and try again.'}`);
      });

      if (!response.ok) {
        let errorMessage = 'An error occurred during checkout.';
        try {
          // Try to parse error response
          const errorData = await response.text();
          errorMessage = `Server error (${response.status}): ${errorData || response.statusText}`;
        } catch (e) {
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const { sessionId } = await response.json().catch(() => {
        throw new Error('Invalid response from server. Please try again later.');
      });

      // Check if Stripe public key is defined
      if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
        throw new Error('Payment processing is not configured. Please contact support.');
      }

      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY).catch(() => {
        throw new Error('Payment system initialization failed. Please try again later.');
      });
      
      if (!stripe) {
        throw new Error('Payment system is unavailable. Please try again later.');
      }
      
      // Only pass sessionId to redirectToCheckout
      const result = await stripe.redirectToCheckout({ sessionId });
      
      if (result.error) {
        throw new Error(`Payment error: ${result.error.message}`);
      }
    } catch (error) {
      console.error('Detailed checkout error:', error);
      analytics.error(
        'checkout_error',
        error.message || 'An unexpected error occurred during checkout',
        'PricingSection'
      );
      setCheckoutError(error.message || 'An unexpected error occurred during checkout. Please try again later.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section className="pricing-section">
      <div className="pricing-container">
        <h2>Order Details</h2>
        
        <div className="quantity-selector">
          <h3>Quantity</h3>
          <div className="quantity-controls">
            <button
              className="quantity-button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              -
            </button>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="quantity-input"
            />
            <button
              className="quantity-button"
              onClick={() => setQuantity(quantity + 1)}
            >
              +
            </button>
          </div>
        </div>

        <div className="shipping-options">
          <h3>Shipping Options</h3>
          <div className="shipping-option">
            <input
              type="radio"
              id="standard-shipping"
              name="shipping"
              checked={!isExpressShipping}
              onChange={() => setIsExpressShipping(false)}
            />
            <label htmlFor="standard-shipping">
              Standard Shipping (${standardShippingPrice.toFixed(2)})
            </label>
          </div>
          <div className="shipping-option">
            <input
              type="radio"
              id="express-shipping"
              name="shipping"
              checked={isExpressShipping}
              onChange={() => setIsExpressShipping(true)}
            />
            <label htmlFor="express-shipping">
              Express Shipping (${expressShippingPrice.toFixed(2)})
            </label>
          </div>
        </div>

        <div className="coupon-section">
          <h3>Coupon Code</h3>
          <div className="coupon-input-container">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              className="coupon-input"
            />
            <button
              className="apply-coupon-button"
              onClick={handleApplyCoupon}
            >
              Apply
            </button>
          </div>
          {couponDiscount > 0 && (
            <p className="coupon-applied">Coupon applied: -${couponDiscount.toFixed(2)}</p>
          )}
        </div>

        <div className="price-summary">
          <div className="price-details">
            <div className="price-row">
              <span>Price per tile:</span>
              <span>${basePrice.toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Quantity:</span>
              <span>{quantity}</span>
            </div>
            <div className="price-row">
              <span>Subtotal:</span>
              <span>${(basePrice * quantity).toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Shipping:</span>
              <span>${(isExpressShipping ? expressShippingPrice : standardShippingPrice).toFixed(2)}</span>
            </div>
            {couponDiscount > 0 && (
              <div className="price-row discount">
                <span>Discount:</span>
                <span>-${couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="price-row total">
              <span>Total:</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
          </div>
          
          {checkoutError && (
            <div className="checkout-error">
              <p>{checkoutError}</p>
              <p className="error-help">
                If this problem persists, please contact our support team at support@printmytile.com
              </p>
            </div>
          )}
          
          <button 
            className={`checkout-button ${isProcessing ? 'processing' : ''}`}
            onClick={handleCheckout}
            disabled={!originalImage || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection; 